<template>
	<div class="sub-bar">
		<template v-if="isFirstRowUnlocked">
			<SubResource
				:key="getPanelResource(0)"
				:iconName="getPanelResource(0)"
				:value="getResourceValue(getPanelResource(0))"
				:progress="getResourceProgress(getPanelResource(0))"
				:isMax="isMax(getPanelResource(0))"
				:max="getResourceStorageValue(getPanelResource(0))"
				:unlockLevel="getUnlockLevel(getPanelResource(0))"
				:isOverMax="isOverMax(getPanelResource(0))"
				:isLocked="isResourceLocked(getPanelResource(0))"
				hasDivider
				:dividerShape="[0, 1, 0, 0]"
			/>
			<SubResource
				:key="getPanelResource(1)"
				:iconName="getPanelResource(1)"
				:value="getResourceValue(getPanelResource(1))"
				:progress="getResourceProgress(getPanelResource(1))"
				:isMax="isMax(getPanelResource(1))"
				:max="getResourceStorageValue(getPanelResource(1))"
				:unlockLevel="getUnlockLevel(getPanelResource(1))"
				:isOverMax="isOverMax(getPanelResource(1))"
				:isLocked="isResourceLocked(getPanelResource(1))"
				hasDivider
				:dividerShape="[0, 1, 0, 1]"
			/>
			<SubResource
				:key="getPanelResource(2)"
				:iconName="getPanelResource(2)"
				:value="getResourceValue(getPanelResource(2))"
				:progress="getResourceProgress(getPanelResource(2))"
				:isMax="isMax(getPanelResource(2))"
				:max="getResourceStorageValue(getPanelResource(2))"
				:unlockLevel="getUnlockLevel(getPanelResource(2))"
				:isOverMax="isOverMax(getPanelResource(2))"
				:isLocked="isResourceLocked(getPanelResource(2))"
				:dividerShape="[0, 0, 0, 1]"
			/>
		</template>
		<template v-if="isSecondRowUnlocked">
			<SubResource
				:key="getPanelResource(3)"
				:iconName="getPanelResource(3)"
				:value="getResourceValue(getPanelResource(3))"
				:progress="getResourceProgress(getPanelResource(3))"
				:isMax="isMax(getPanelResource(3))"
				:max="getResourceStorageValue(getPanelResource(3))"
				:unlockLevel="getUnlockLevel(getPanelResource(3))"
				:isOverMax="isOverMax(getPanelResource(3))"
				:isLocked="isResourceLocked(getPanelResource(3))"
				:dividerShape="[0, 1, 0, 0]"
				hasDivider
			/>
			<SubResource
				:key="getPanelResource(4)"
				:iconName="getPanelResource(4)"
				:value="getResourceValue(getPanelResource(4))"
				:progress="getResourceProgress(getPanelResource(4))"
				:isMax="isMax(getPanelResource(4))"
				:max="getResourceStorageValue(getPanelResource(4))"
				:unlockLevel="getUnlockLevel(getPanelResource(4))"
				:isOverMax="isOverMax(getPanelResource(4))"
				:isLocked="isResourceLocked(getPanelResource(4))"
				:dividerShape="[0, 1, 0, 1]"
				hasDivider
			/>
			<SubResource
				:key="getPanelResource(5)"
				:iconName="getPanelResource(5)"
				:value="getResourceValue(getPanelResource(5))"
				:progress="getResourceProgress(getPanelResource(5))"
				:isMax="isMax(getPanelResource(5))"
				:max="getResourceStorageValue(getPanelResource(5))"
				:unlockLevel="getUnlockLevel(getPanelResource(5))"
				:isOverMax="isOverMax(getPanelResource(5))"
				:isLocked="isResourceLocked(getPanelResource(5))"
				:dividerShape="[0, 1, 0, 1]"
				hasDivider
			/>
		</template>
	</div>
</template>

<script lang="ts" setup>
import SubResource from "@/components/SubResource.vue";
import { ResourceType } from "@/game/entities/config/resources";
import useResourcesStorageStore from "@/store/useResourcesStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import useUserStore from "@/store/useUserStore";
import { storeToRefs } from "pinia";

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);

const resourcesStorageStore = useResourcesStorageStore();
const resourcesStorage = storeToRefs(resourcesStorageStore);

const { resourcePanelConfig } = storeToRefs(useUserStore());
interface Props {
	resourcesUnlocks: Partial<Record<ResourceType, boolean>>;
	isFirstRowUnlocked: boolean;
	isSecondRowUnlocked: boolean;
	unlockLevels: Partial<Record<ResourceType, number>>;
}

const {
	resourcesUnlocks,
	isFirstRowUnlocked,
	isSecondRowUnlocked,
	unlockLevels,
} = defineProps<Props>();

const getPanelResource = (index: number): ResourceType => {
	return (
		(resourcePanelConfig.value?.[index] as ResourceType) ??
		ResourceType.COINS
	);
};

const getResourceValue = (resource: ResourceType): number => {
	return resources[resource]?.value ?? 0;
};

const getResourceProgress = (resource: ResourceType): number => {
	const current = resources[resource]?.value ?? 0;
	const storage = resourcesStorage[resource]?.value ?? 0;
	return storage === 0 ? 0 : Math.min(current / storage, 1);
};

const getResourceStorageValue = (resource: ResourceType): number => {
	return resourcesStorage[resource]?.value ?? 0;
};

const getUnlockLevel = (resource: ResourceType): number => {
	return unlockLevels[resource] ?? 0;
};

const isResourceLocked = (resource: ResourceType): boolean => {
	return !resourcesUnlocks[resource];
};

const isMax = (resource: ResourceType): boolean => {
	return (
		resources[resource]?.value !== 0 &&
		resourcesStorage[resource]?.value <= resources[resource]?.value
	);
};

const isOverMax = (resource: ResourceType): boolean => {
	return (
		resources[resource]?.value !== 0 &&
		resourcesStorage[resource]?.value < resources[resource]?.value
	);
};
</script>

<style lang="scss" scoped>
.sub-bar {
	display: flex;
	flex: 1;
	flex-wrap: wrap;
	justify-content: center;
	margin-top: 0.188rem;

	.sub-resource {
		flex: 1 1 calc(100% / 3);

		&:nth-child(-n + 3) {
			flex-basis: calc(100% / 3); /* first 3 items evenly spaced */
		}

		&:nth-child(n + 4) {
			flex-basis: calc(100% / 4); /* remaining 4 per row */
			margin-top: 0.188rem;
		}
	}
}
</style>
